import { useCallback, useContext, useMemo } from 'react';
import { getDurationString } from '../Utils/utils';
import { bookingState } from '../Pages/Booking/client-state/booking-state-context';
import useBookingShop from '../Pages/Booking/server-data/use-booking-shop';
import useBookingDockingLocations from '../Pages/Booking/server-data/use-booking-docking-locations';

const useBookingPrices = () => {
  const { data: shop } = useBookingShop();
  const { data: dockingLocations } = useBookingDockingLocations();
  const { state } = useContext(bookingState);

  const getPriceModel = useCallback(
    (priceModelId?: string | null) => {
      if (!priceModelId) return undefined;

      return shop?.priceModels.find((priceModel) => priceModel.id === priceModelId);
    },
    [shop?.priceModels]
  );
  const calculateBookingPrices = useCallback(
    (customPriceModelId?: string) => {
      const bookingPriceModel = getPriceModel(state.booking.priceModelId);
      const customPriceModel = getPriceModel(customPriceModelId);
      const priceModel = customPriceModel ?? bookingPriceModel;
      const sailingPrice = state.booking?.priceBreakdown?.boatRentalPrice ?? 0;
      const pickupLocationPrice = state.booking?.priceBreakdown?.pickupLocationPrice ?? 0;
      const dropoffLocationPrice =
        state.booking?.priceBreakdown?.dropoffLocationPrice ?? 0;

      if (priceModel && priceModel?.durations) {
        // These keys should be synced with the "priceBreakdown" from the booking-api
        const prices = {
          rentalPrice: 0,
          voucherValue: 0,
          voucherValueAddons: 0,
          sailingPrice: 0,
          pickupPrice: 0,
          dropoffPrice: 0,
          extrasPrice: 0,
          donationValue: 0,
          warrantyPrice: 0,
          paymentValue: 0,
          fullPrice: 0,
          balance: 0,
          toBePaid: 0,
          toBeRefunded: 0,
        };

        const addons = state.order?.filter((addon) => {
          const item = shop?.items?.find((item) => item.id === addon.itemId);
          return item ? !item.donation : false;
        });
        const donations = state.order?.filter((addon) => {
          const item = shop?.items?.find((item) => item.id === addon.itemId);
          return item ? item.donation === true : false;
        });

        const durationString = getDurationString(state.booking.duration);
        const duration = priceModel.durations.find((d) => d.duration === durationString);
        const durationPrice = duration?.price ?? 0;

        prices.rentalPrice = durationPrice * state.booking.boatCount;

        if (state.booking.pickupLocationId && priceModel?.dockingLocationZonePrices) {
          const pickupLocation = dockingLocations?.find(
            (location) => location.id === state.booking.pickupLocationId
          );
          const zonePrice = priceModel.dockingLocationZonePrices.find(
            (zonePrice) => zonePrice.dockingLocationZoneId === pickupLocation?.zoneId
          );

          if (zonePrice) {
            prices.pickupPrice = zonePrice.price * state.booking.boatCount;
          }
        }

        if (state.booking.dropoffLocationId && priceModel?.dockingLocationZonePrices) {
          const dropoffLocation = dockingLocations?.find(
            (location) => location.id === state.booking.dropoffLocationId
          );
          const zonePrice = priceModel.dockingLocationZonePrices.find(
            (zonePrice) => zonePrice.dockingLocationZoneId === dropoffLocation?.zoneId
          );

          if (zonePrice) {
            prices.dropoffPrice = zonePrice.price * state.booking.boatCount;
          }
        }

        prices.sailingPrice =
          prices.rentalPrice + prices.pickupPrice + prices.dropoffPrice;

        if (state.vouchers) {
          prices.voucherValue = 0;
          for (const voucher of state.vouchers) {
            const isNotValidForExtras =
              !voucher.validFor ||
              (voucher.validFor && !voucher.validFor.includes('extras'));

            if (isNotValidForExtras) {
              if (voucher.type === 'percent') {
                prices.voucherValue += (prices.sailingPrice / 100) * voucher.value;
              } else {
                prices.voucherValue += voucher.value;
              }
            }
          }
        }

        prices.sailingPrice = prices.sailingPrice - prices.voucherValue;
        if (prices.sailingPrice < 0) prices.sailingPrice = 0;

        if (addons && shop?.items && shop?.items?.length > 0) {
          for (const addon of addons) {
            const item = shop?.items.find((item) => item.id === addon.itemId);

            if (item) {
              prices.extrasPrice += addon.quantity * item.price;
            }
          }
        }

        if (state.payment.payments) {
          for (const payment of state.payment.payments) {
            prices.paymentValue += payment.amount;
          }
        }

        prices.fullPrice = prices.sailingPrice + prices.extrasPrice;

        if (state.vouchers) {
          prices.voucherValueAddons = 0;
          for (const voucher of state.vouchers) {
            const isValidForExtras =
              voucher.validFor && voucher.validFor.includes('extras');

            if (isValidForExtras) {
              if (voucher.type === 'percent') {
                prices.voucherValueAddons += (prices.fullPrice / 100) * voucher.value;
              } else {
                prices.voucherValueAddons += voucher.value;
              }
            }
          }
        }

        prices.fullPrice = prices.fullPrice - prices.voucherValueAddons;
        if (prices.fullPrice < 0) prices.fullPrice = 0;

        if (state.isWarrantyApplied) {
          let warrantyPrice = priceModel.cancellationWarranty?.price ?? 0;

          if (priceModel.cancellationWarranty?.per === 'boat') {
            warrantyPrice *= state.booking.boatCount;
          }

          prices.warrantyPrice = warrantyPrice;
        }

        prices.fullPrice += prices.warrantyPrice;

        if (donations && shop?.items && shop?.items?.length > 0) {
          for (const addon of donations) {
            const item = shop?.items.find((item) => item.id === addon.itemId);

            if (item) {
              prices.donationValue += addon.quantity * item.price;
            }
          }
        }
        prices.fullPrice += prices.donationValue;

        const breakdownTotalPrice = state.booking?.priceBreakdown?.totalPrice;
        const finalTotalPrice =
          typeof breakdownTotalPrice === 'number'
            ? breakdownTotalPrice
            : prices.fullPrice;

        prices.balance = prices.paymentValue - finalTotalPrice;
        // If the balance is negative (customer needs to pay),
        // turn the negative balance in to a positive toBePaid value.
        // If the balance is positive, (customer have paid to much),
        // set the toBePaid value to 0.
        prices.toBePaid = prices.balance < 0 ? Math.abs(prices.balance) : 0;
        // If the balance is negative (customer needs to pay),
        // set the toBeRefunded to 0.
        // If the balance is positive, (customer have paid to much),
        // set the toBeRefunded to the balance.
        prices.toBeRefunded = prices.balance > 0 ? prices.balance : 0;

        return prices;
      }

      return {
        rentalPrice: sailingPrice,
        pickupPrice: pickupLocationPrice,
        dropoffPrice: dropoffLocationPrice,
        voucherValue: undefined,
        voucherValueAddons: undefined,
        sailingPrice,
        extrasPrice: undefined,
        donationValue: undefined,
        warrantyPrice: undefined,
        paymentValue: undefined,
        fullPrice: undefined,
        balance: undefined,
        toBePaid: undefined,
        toBeRefunded: undefined,
      };
    },
    [
      state.booking.boatCount,
      state.booking.dropoffLocationId,
      state.booking.duration,
      state.booking.pickupLocationId,
      state.booking.priceModelId,
      state.booking?.priceBreakdown?.boatRentalPrice,
      state.booking?.priceBreakdown?.pickupLocationPrice,
      state.booking?.priceBreakdown?.dropoffLocationPrice,
      state.booking?.priceBreakdown?.totalPrice,
      shop?.items,
      state.order,
      state.payment.payments,
      dockingLocations,
      state.vouchers,
      state.isWarrantyApplied,
      getPriceModel,
    ]
  );

  const bookingPrices = useMemo(() => {
    if (state.booking?.priceBreakdown) {
      const priceBreakdown = state.booking.priceBreakdown;
      const paymentsTotal =
        state.payment.payments?.reduce((sum, p) => sum + p.amount, 0) ?? 0;

      const balance = paymentsTotal - priceBreakdown.totalPrice;
      return {
        rentalPrice: priceBreakdown.boatRentalPrice ?? 0,
        pickupPrice: priceBreakdown.pickupLocationPrice ?? 0,
        dropoffPrice: priceBreakdown.dropoffLocationPrice ?? 0,
        sailingPrice: priceBreakdown.boatRentalPriceWithDiscount,
        extrasPrice: priceBreakdown.addonsPriceWithDiscount,
        warrantyPrice: priceBreakdown.cancellationWarrantyPrice,
        fullPrice: priceBreakdown.totalPrice,
        voucherValue: priceBreakdown.voucherValueBoatRental,
        voucherValueAddons: priceBreakdown.voucherValueAddons,
        balance,
        toBePaid: balance < 0 ? Math.abs(balance) : 0,
        toBeRefunded: balance > 0 ? balance : 0,
      };
    }

    return calculateBookingPrices();
  }, [state.booking?.priceBreakdown, state.payment.payments, calculateBookingPrices]);

  return { bookingPrices, calculateBookingPrices };
};

export default useBookingPrices;
